import DeviceDetector from 'device-detector-js'
import React, { useEffect, useState, useCallback } from 'react'
import { connect } from 'react-redux'
import {
    BrowserRouter,
    Route,
    Switch,
    useHistory,
    withRouter,
} from 'react-router-dom'
import { MathJaxContext } from 'better-react-mathjax'
import Header from '../src/components/Header/Header'
import SubmitPage from '../src/components/SubmitPage/SubmitPage'
import {
    UPDATE_DEVICE_ID,
    UPDATE_PUSH_NOTIFICATION_EVENT,
} from '../src/constants/actionTypes'
import InstructionsPage from '../src/views/InstructionsPage/InstructionsPage'
import TestAnalytics from '../src/views/TestAnalytics/TestAnalytics'
import TestPanel from '../src/views/TestPanel/TestPanel'
import Thankyou from '../src/views/Thankyou/Thankyou'
import './App.css'
import InternetIssues from './components/InternetIssue/InternetIssue'
import Results from './views/Results/Results'
import { isInPhoneOrTablet } from './utils/common/isInPhoneOrTablet'
import gtagManager from './gtagManager'
import { MATHJAX_2_CONFIG } from 'constants/mathjaxConfig'

const mapStateToProps = (state) => ({
    active_section: state.active_section,
    header_visible: state.header_visible,
    instructions: state.instructions,
    packageId: state.packageId,
    get_ip_address: state.get_ip_address,
    queryparam: state.queryparam,
    persistor: state.persistor,
    testStatus: state.teststatus,
})

const mapDispatchToProps = (dispatch) => ({
    setDeviceId: (payload) => dispatch({ type: UPDATE_DEVICE_ID, payload }),
    setPushNotificationEvent: (payload) =>
        dispatch({ type: UPDATE_PUSH_NOTIFICATION_EVENT, payload }),
})

const App = (props) => {
    const [isHeaderVisible, setIsHeaderVisible] = useState(true)
    const [active, setActive] = useState(props.active_section)
    const history = useHistory()

    const isPreview =
        sessionStorage.getItem('tp_preview_token') &&
        sessionStorage.getItem('test_id')
            ? true
            : false
    const mathjaxStartupHandler = useCallback((mathJax) => {
        mathJax.Hub.processSectionDelay = 0
        mathJax.Hub.processUpdateDelay = 0
        function retryTypeset(errorStr, message) {
            console.error(errorStr, message, message[1], message[2])
            window.MathJax?.Hub?.Queue(() => window.MathJax.Hub.Typeset())
        }
        function mathErrorHandler(message) {
            retryTypeset('mathjax_error - Math Processing Error', message)
        }
        function texErrorHandler(message) {
            retryTypeset('mathjax_error - TeX Jax - parse error', message)
        }
        // Docs: https://docs.mathjax.org/en/v2.7-latest/advanced/debugging-tips.html
        window.MathJax?.Hub?.Register.MessageHook(
            'Math Processing Error',
            mathErrorHandler,
        )
        window.MathJax?.Hub?.Register.MessageHook(
            'TeX Jax - parse error',
            texErrorHandler,
        )
    }, [])

    useEffect(() => {
        if (
            props.location.pathname !== '/success' &&
            props.location.pathname !== '/testpanel' &&
            props.location.pathname !== '/' &&
            window.location.href.slice(window.location.href.length - 8) !==
                '/success' &&
            window.location.href.slice(window.location.href.length - 8) !==
                '/results' &&
            props.header_visible &&
            Object.keys(props.instructions).length
        ) {
            setIsHeaderVisible(false)
        } else if (!isHeaderVisible) {
            setIsHeaderVisible(true)
        }
    }, [window.location.href])

    // Send groupExtraData as true when you want to group the device info into a single event parameter. This way, we can
    // send 6 extra parameters to firebase/gtag/bigquery (limit: 15).
    const handleGTagLogs = async (
        eventName,
        description,
        data,
        groupExtraData = false,
    ) => {
        // Firebase/Gtag logs are not required for test preview
        if (isPreview) return
        if (
            window.location.protocol === 'https:' ||
            window.location.host === 'localhost:3000'
        ) {
            const deviceDetector = new DeviceDetector()
            const device = deviceDetector.parse(navigator.userAgent)
            const extraData = {
                package: props.packageId,
                device: device.client?.name,
                browser: device.client?.name,
                model: device.device?.brand + device.os?.version + device.os?.name,
                manufacturer: device.device?.brand,
                // app_version: "version",
                ip_address: props.get_ip_address,
                // web: true,
                web_resposive: isInPhoneOrTablet(),
            }

            let commonData = {
                // time_in: new Date().toISOString(),
                class: props.queryparam.class_studying,
                roll_no: props.queryparam.roll_no,
            }
            let otherCommonTestData = {
                dob: props.queryparam?.dob,
                stream: props.queryparam?.stream,
                test_short_code: props.queryparam.test_short_code,
                grace_period: props.queryparam?.grace_period,
                test_id: props.instructions.testData.id,
                test_name: props.instructions.testData.titleEn,
                duration_of_test: props.instructions.testData.allowedDuration,
                disconnection_time: props.queryparam.disconnect_time * 60,
                total_questions: props.instructions.testData.totalQuestion,
                max_marks: props.instructions.testData.maxMarks,
            }

            const _data = {
                ...extraData,
                ...commonData,
                ...otherCommonTestData,
                ...data,
            }
            gtagManager({ eventName, eventData: _data })
            // await analytics.logEvent(eventName, _data)
        }
    }

    // Removed setPushNotification state update, its not being used anywhere

    return (
        <BrowserRouter>
            <MathJaxContext
                config={MATHJAX_2_CONFIG}
                version={2}
                hideUntilTypeset="first"
                // to handle flicker
                onStartup={mathjaxStartupHandler}
                onError={(error) => {
                    console.error('error while rendering mathjax content:', error)
                }}
            >
                <div className="App" id="test_player">
                    {props.header_visible ? (
                        <Header
                            isPreview={isPreview}
                            handleGTagLogs={handleGTagLogs}
                            style={
                                props.location.pathname === '/submit' &&
                                isInPhoneOrTablet()
                                    ? { height: '90px' }
                                    : null
                            }
                        />
                    ) : null}
                    <Switch>
                        <Route
                            exact
                            path="/"
                            render={() => (
                                <InstructionsPage handleGTagLogs={handleGTagLogs} />
                            )}
                        />
                        <Route
                            path="/testpanel"
                            render={(props) => (
                                <TestPanel
                                    handleGTagLogs={handleGTagLogs}
                                    active={active}
                                    setActive={setActive}
                                />
                            )}
                        />
                        <Route
                            path="/submit"
                            render={(props) => (
                                <SubmitPage
                                    handleGTagLogs={handleGTagLogs}
                                    persistor={props.persistor}
                                />
                            )}
                        />
                        <Route
                            path="/success"
                            render={(props) => (
                                <Thankyou
                                    handleGTagLogs={handleGTagLogs}
                                    isPreview={isPreview}
                                />
                            )}
                        />
                        <Route
                            path="/analytics"
                            component={(props) => (
                                <TestAnalytics setActive={setActive} {...props} />
                            )}
                            handleGTagLogs={handleGTagLogs}
                        />
                        <Route
                            path="/results"
                            render={(props) => (
                                <Results handleGTagLogs={handleGTagLogs} />
                            )}
                        />
                        <Route
                            path="/offline"
                            component={InternetIssues}
                            handleGTagLogs={handleGTagLogs}
                        />
                    </Switch>
                </div>
            </MathJaxContext>
        </BrowserRouter>
    )
}
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App))
